// Copy a string to the clipboard and schedule a clear after `clearMs`.
// Shows a toast with a live countdown. Re-copying cancels the previous timer.

import { el } from './dom.js';

const CLEAR_MS = 30_000;
let currentTimer = null;
let countdownTimer = null;
let toastNode = null;
let pendingRetry = null;   // teardown for the deferred-clear listeners

export async function copyWithAutoClear(text, label = 'Copied') {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.warn('clipboard write failed', err);
    return;
  }

  cancel();

  const deadline = Date.now() + CLEAR_MS;
  toastNode = el('div', { class: 'toast' }, [
    `${label}`,
    el('span', { class: 'count' }, [`clears in ${Math.ceil(CLEAR_MS / 1000)}s`]),
  ]);
  document.body.append(toastNode);

  countdownTimer = setInterval(() => {
    const left = Math.max(0, deadline - Date.now());
    if (toastNode) {
      const count = toastNode.querySelector('.count');
      if (count) count.textContent = `clears in ${Math.ceil(left / 1000)}s`;
    }
  }, 500);

  currentTimer = setTimeout(() => { runClear(); }, CLEAR_MS);
}

// Overwrite the clipboard. Resolves true only if the write actually landed.
async function tryClear() {
  try {
    await navigator.clipboard.writeText('');
    return true;
  } catch {
    return false;
  }
}

// The clear runs 30s after the copy — by which time the user has very often
// switched to another tab or app to paste, which is the whole point of copying.
// navigator.clipboard.writeText rejects with NotAllowedError when the document
// isn't focused, so the naive version failed exactly in the common case while
// still reporting "Clipboard cleared". Retry when focus comes back, and never
// claim success we didn't get.
async function runClear() {
  if (await tryClear()) {
    finish('Clipboard cleared');
    return;
  }
  showMessage('Clipboard clears when you return to this tab');
  const retry = async () => {
    if (!(await tryClear())) return;   // still not focused; wait for the next event
    teardownRetry();
    finish('Clipboard cleared');
  };
  const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
  window.addEventListener('focus', retry);
  document.addEventListener('visibilitychange', onVisible);
  pendingRetry = () => {
    window.removeEventListener('focus', retry);
    document.removeEventListener('visibilitychange', onVisible);
    pendingRetry = null;
  };
}

function teardownRetry() {
  if (pendingRetry) pendingRetry();
}

function showMessage(msg) {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  if (toastNode) toastNode.textContent = msg;
}

function finish(msg) {
  if (currentTimer) { clearTimeout(currentTimer); currentTimer = null; }
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  if (toastNode) {
    toastNode.textContent = msg;
    const node = toastNode;
    toastNode = null;
    setTimeout(() => node.remove(), 1000);
  }
}

function cancel() {
  if (currentTimer) clearTimeout(currentTimer);
  if (countdownTimer) clearInterval(countdownTimer);
  currentTimer = null;
  countdownTimer = null;
  teardownRetry();
  if (toastNode) { toastNode.remove(); toastNode = null; }
}
